import { useEffect, useState, useRef } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Database, Download, Upload, RefreshCw, ShieldCheck, Clock,
  HardDrive, Trash2, ArrowDownToLine, AlertTriangle, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function BackupRestore() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [autoOn, setAutoOn] = useState(true);
  const [restoreOpen, setRestoreOpen] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [dropExisting, setDropExisting] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const fileRef = useRef(null);

  const fetchList = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/backup/list");
      setList(data.backups || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load backups");
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchList(); }, []);

  // Client-side role guard
  if (user && user.role !== "super_admin" && !user.is_platform_owner) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center" data-testid="backup-restore-denied">
        <Card className="p-10 text-center max-w-md">
          <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 mx-auto mb-4 flex items-center justify-center">
            <ShieldCheck className="w-7 h-7" />
          </div>
          <div className="text-lg font-bold">Super-admin only</div>
          <p className="text-sm text-slate-500 mt-2">Backup &amp; Restore is restricted to workspace super-admins to prevent accidental data loss.</p>
        </Card>
      </div>
    );
  }

  const createBackup = async () => {
    setCreating(true);
    try {
      await api.post("/backup/create");
      toast.success("Backup created");
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Backup failed");
    } finally { setCreating(false); }
  };

  const downloadBackup = async (id) => {
    try {
      const res = await api.get(`/backup/${id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `qrewhr-backup-${id.slice(-8)}.json`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Download started");
    } catch {
      toast.error("Download failed");
    }
  };

  const toggleAuto = async () => {
    try {
      const { data } = await api.post("/backup/auto/toggle");
      setAutoOn(data.auto_backup_enabled);
      toast.success(`Auto-backup ${data.auto_backup_enabled ? "enabled" : "disabled"}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const cleanOld = async () => {
    if (!window.confirm("Delete all backups older than 90 days?")) return;
    try {
      const { data } = await api.post("/backup/clean", null, { params: { retention_days: 90 } });
      toast.success(`Cleaned ${data.deleted} old backups`);
      fetchList();
    } catch { toast.error("Cleanup failed"); }
  };

  const submitRestore = async () => {
    if (!restoreFile) return toast.error("Choose a backup file first");
    setRestoring(true);
    try {
      const fd = new FormData();
      fd.append("file", restoreFile);
      const { data } = await api.post(`/backup/restore?drop_existing=${dropExisting}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Restore complete — ${Object.keys(data.restored || {}).length} collections`);
      setRestoreOpen(false);
      setRestoreFile(null);
      fetchList();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Restore failed");
    } finally { setRestoring(false); }
  };

  return (
    <div className="space-y-6" data-testid="backup-restore-page">
      {/* Header */}
      <div className="relative overflow-hidden rounded-3xl border border-slate-200 bg-gradient-to-br from-slate-900 to-indigo-900 text-white shadow-xl">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_30%,rgba(6,182,212,0.18),transparent_40%),radial-gradient(circle_at_85%_70%,rgba(255,122,42,0.18),transparent_40%)]" />
        <div className="relative p-8 sm:p-10 flex flex-col lg:flex-row lg:items-center gap-6">
          <div className="flex-1">
            <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-emerald-100/15 border border-emerald-300/30 text-emerald-300 text-[10px] uppercase tracking-[0.2em] font-bold mb-3">
              <ShieldCheck className="w-3 h-3" /> Tenant-isolated backups
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" data-testid="backup-title">Backup & Restore</h1>
            <p className="text-slate-300 text-sm sm:text-base mt-2 max-w-2xl">
              Snapshot your full tenant — employees, attendance, leaves, payroll, assets, claims, documents — into a single signed JSON. Restore in seconds.
            </p>
          </div>
          <div className="hidden lg:flex w-32 h-32 rounded-3xl bg-white/5 border border-white/10 backdrop-blur items-center justify-center shrink-0">
            <Database className="w-16 h-16 text-cyan-300" strokeWidth={1.4} />
          </div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Card className="p-5 border-slate-200" data-testid="backup-action-create">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 text-emerald-700 flex items-center justify-center mb-3">
            <ArrowDownToLine className="w-5 h-5" />
          </div>
          <div className="font-semibold text-slate-900">Create snapshot</div>
          <div className="text-xs text-slate-500 mt-1">Captures every collection scoped to your tenant.</div>
          <Button onClick={createBackup} disabled={creating} className="mt-4 w-full bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="backup-create-btn">
            {creating ? <><RefreshCw className="w-3 h-3 mr-1 animate-spin" /> Creating…</> : <>Create now</>}
          </Button>
        </Card>

        <Card className="p-5 border-slate-200" data-testid="backup-action-restore">
          <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center mb-3">
            <Upload className="w-5 h-5" />
          </div>
          <div className="font-semibold text-slate-900">Restore</div>
          <div className="text-xs text-slate-500 mt-1">Upload a previous .json backup to reset your tenant.</div>
          <Button onClick={() => setRestoreOpen(true)} variant="outline" className="mt-4 w-full" data-testid="backup-restore-btn">
            Choose file
          </Button>
        </Card>

        <Card className="p-5 border-slate-200" data-testid="backup-action-auto">
          <div className="w-10 h-10 rounded-xl bg-cyan-100 text-cyan-700 flex items-center justify-center mb-3">
            <Clock className="w-5 h-5" />
          </div>
          <div className="font-semibold text-slate-900 flex items-center justify-between">
            Auto-backup
            <Switch checked={autoOn} onCheckedChange={toggleAuto} data-testid="auto-backup-switch" />
          </div>
          <div className="text-xs text-slate-500 mt-1">Daily snapshot at 02:00 UTC, 90-day retention.</div>
        </Card>

        <Card className="p-5 border-slate-200" data-testid="backup-action-clean">
          <div className="w-10 h-10 rounded-xl bg-rose-100 text-rose-700 flex items-center justify-center mb-3">
            <Trash2 className="w-5 h-5" />
          </div>
          <div className="font-semibold text-slate-900">Cleanup</div>
          <div className="text-xs text-slate-500 mt-1">Purge backups older than 90 days.</div>
          <Button onClick={cleanOld} variant="outline" className="mt-4 w-full text-rose-700 border-rose-200 hover:bg-rose-50" data-testid="backup-clean-btn">
            Clean old
          </Button>
        </Card>
      </div>

      {/* Backups list */}
      <Card className="border-slate-200" data-testid="backup-list-card">
        <div className="p-5 border-b flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-slate-900">Recent snapshots</div>
            <div className="text-xs text-slate-500">{list.length} backup{list.length !== 1 ? "s" : ""} stored</div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchList} disabled={loading} data-testid="backup-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
        <div className="divide-y">
          {loading && (
            <div className="p-8 text-center text-sm text-slate-500">Loading backups…</div>
          )}
          {!loading && list.length === 0 && (
            <div className="p-10 text-center" data-testid="backup-empty">
              <HardDrive className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm font-semibold text-slate-700">No backups yet</div>
              <div className="text-xs text-slate-500 mt-1">Create your first snapshot above.</div>
            </div>
          )}
          {!loading && list.map((b) => (
            <div key={b.id} className="p-4 flex items-center gap-4 hover:bg-slate-50 transition" data-testid={`backup-row-${b.id}`}>
              <div className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 flex items-center justify-center shrink-0">
                <Database className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <div className="font-mono text-xs text-slate-700">{b.id}</div>
                  <Badge variant="outline" className="text-[10px] uppercase tracking-wider">
                    {b.trigger || "manual"}
                  </Badge>
                  {b.status === "ok" && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {fmtDate(b.created_at)} · {fmtBytes(b.size_bytes)} · {b.collection_count || 0} collections
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => downloadBackup(b.id)} data-testid={`backup-download-${b.id}`}>
                <Download className="w-3.5 h-3.5 mr-1" /> Download
              </Button>
            </div>
          ))}
        </div>
      </Card>

      {/* Restore dialog */}
      <Dialog open={restoreOpen} onOpenChange={setRestoreOpen}>
        <DialogContent data-testid="restore-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" /> Restore from backup
            </DialogTitle>
            <DialogDescription>
              This will replace data in your tenant from the uploaded snapshot. Make sure to download a current backup first.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div>
              <input
                ref={fileRef}
                type="file"
                accept=".json,application/json"
                onChange={(e) => setRestoreFile(e.target.files?.[0] || null)}
                className="block w-full text-sm text-slate-700 file:mr-3 file:py-2 file:px-3 file:rounded-md file:border-0 file:text-xs file:font-semibold file:bg-cyan-50 file:text-cyan-700 hover:file:bg-cyan-100"
                data-testid="restore-file-input"
              />
              {restoreFile && (
                <div className="text-[11px] text-slate-500 mt-2">
                  Selected: <b>{restoreFile.name}</b> ({fmtBytes(restoreFile.size)})
                </div>
              )}
            </div>
            <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <Switch checked={dropExisting} onCheckedChange={setDropExisting} data-testid="drop-existing-switch" />
              <div className="text-xs text-amber-900">
                <b>Drop existing data first</b> — recommended for clean restore.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRestoreOpen(false)}>Cancel</Button>
            <Button onClick={submitRestore} disabled={restoring || !restoreFile} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="restore-submit-btn">
              {restoring ? "Restoring…" : "Restore now"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
